import Constants from '@/Constants';
import EmptyState from '@/components/EmptyState';
import LicenceUse from '@/components/LicenceUse';
import Loader from '@/components/Loader';
import Tab from '@/components/Tab';
import { OrderBy } from '@/models/OrderBy';
import { PaginationData } from '@/models/PaginationData';
import { Person, PersonRequest } from '@/models/Person';
import { ApplicationState } from '@/store';
import { getPeopleClear, getPeopleRequest, getTotalPeopleRequest } from '@/store/person/action';
import React, { useCallback, useEffect, useState } from 'react';
import { useBottomScrollListener } from 'react-bottom-scroll-listener';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import ImportHistory from './ImportHistory';
import PeopleCard from './PeopleCard';
import PeopleSearch from './PeopleSearch';
import ManagerGroup from './PersonGroup/Groups/ManagerGroup';
import './style.scss';

export type PeopleTabs = 'ADMIN' | 'IMPORT' | 'GROUP';

const People: React.FC = () => {
    const { data: strings } = useSelector((state: ApplicationState) => state.locale);
    const {
        data: {
            user: { orgId, applicationId },
        },
    } = useSelector((state: ApplicationState) => state.identity);
    const PAGINATION_LIMIT: number = 20;

    const alphabeticalOrder: OrderBy = { description: strings.ALPHABETICAL_ORDER, value: Constants.USER_ORDER_BY_NAME };

    const [orderBy, setOrderBy] = useState<OrderBy>(alphabeticalOrder);
    const [initialLoading, setInitialLoading] = useState(true);
    const [tabIndex, setTabIndex] = useState<PeopleTabs>('ADMIN');

    const { listOfUser } = useSelector((state: ApplicationState) => state.people.data);
    const { loading: loadingUsers } = useSelector((state: ApplicationState) => state.people);
    const { totalRegistered } = useSelector((state: ApplicationState) => state.people);
    const { list: listOfPerson } = useSelector((state: ApplicationState) => state.people.data.listOfPeople);
    const { total: totalOfPerson } = useSelector((state: ApplicationState) => state.people.data.listOfPeople);
    const [paginationData, setPaginationData] = useState<PaginationData>({ offset: 0, limit: PAGINATION_LIMIT } as PaginationData);

    const dispatch = useDispatch();
    const history = useHistory();

    useEffect(() => {
        dispatch(getPeopleClear());
        if (listOfPerson.length === 0) {
            const searchRequest: PersonRequest = { offset: 0, limit: PAGINATION_LIMIT, orgId, applicationId, orderBy: orderBy.value };
            dispatch(getPeopleRequest(searchRequest));
        }
    }, [orgId, applicationId]);

    useEffect(() => {
        getRegisteredPeopleRequest();
    }, []);

    useEffect(() => {
        if (!loadingUsers && listOfPerson && listOfPerson.length > 0) {
            setInitialLoading(false);
        }
    }, [loadingUsers, listOfUser, listOfPerson]);

    const callback = useCallback(() => {
        if (tabIndex === 'ADMIN' && listOfPerson.length < totalOfPerson) {
            setPaginationData({ offset: listOfPerson.length, limit: PAGINATION_LIMIT } as PaginationData);
        }
    }, [listOfPerson, tabIndex, totalOfPerson, setPaginationData]);

    const getRegisteredPeopleRequest = () => {
        dispatch(
            getTotalPeopleRequest({
                offset: 0,
                limit: 0,
                orgId,
                applicationId,
                orderBy: orderBy.value,
                search: '',
            } as PersonRequest),
        );
    };

    const handleNewPerson = (): void => history.push('/people/create');

    const getUserForPerson = (person: Person): any => listOfUser.find((user) => user.personId === person.id);

    useBottomScrollListener(callback, { offset: 150 });

    return (
        <>
            {initialLoading && <Loader />}
            {!initialLoading && (
                <div className="users">
                    <div className="list">
                        {!initialLoading && <LicenceUse users={totalRegistered} />}
                        <PeopleSearch handleNewPerson={handleNewPerson} paginationData={paginationData} />
                        <>
                            <div className="tabs-container">
                                <Tab
                                    active={tabIndex === 'ADMIN'}
                                    icon="people"
                                    setActive={() => {
                                        setTabIndex('ADMIN');
                                    }}
                                    title={strings.ADMIN_USERS}
                                />
                                <Tab
                                    active={tabIndex === 'IMPORT'}
                                    icon="file_copy"
                                    setActive={() => {
                                        setTabIndex('IMPORT');
                                    }}
                                    title={strings.ADMIN_IMPORT_USERS}
                                />
                                <Tab
                                    active={tabIndex === 'GROUP'}
                                    icon="adjust"
                                    setActive={() => {
                                        setTabIndex('GROUP');
                                    }}
                                    title={strings.MANAGER_GROUP}
                                />
                            </div>
                            <div className="list-container">
                                <div className="list-people">
                                    {tabIndex === 'ADMIN' && (
                                        <>
                                            {!loadingUsers && !listOfPerson && (
                                                <EmptyState
                                                    emptyType={strings.EMPTY_PEOPLE}
                                                    handleCTA={handleNewPerson}
                                                    iconType="person"
                                                    labelCTA={strings.CREATE_NEW_PEOPLE}
                                                />
                                            )}
                                            {listOfPerson.length > 0 && listOfUser.length > 0
                                                ? listOfPerson.map((person: Person) => (
                                                      <PeopleCard key={person.id} person={person} user={getUserForPerson(person)} />
                                                  ))
                                                : !loadingUsers && (
                                                      <EmptyState
                                                          emptyType={strings.EMPTY_PEOPLE}
                                                          handleCTA={handleNewPerson}
                                                          iconType="person"
                                                          labelCTA={strings.CREATE_NEW_PEOPLE}
                                                      />
                                                  )}
                                            {tabIndex === 'ADMIN' && loadingUsers && <Loader />}
                                        </>
                                    )}
                                    {tabIndex === 'IMPORT' && <ImportHistory />}
                                    {tabIndex === 'GROUP' && <ManagerGroup />}
                                </div>
                            </div>
                        </>
                    </div>
                </div>
            )}
        </>
    );
};

export default People;
